/*
* DeivceList.js (Please do not rename this file)
* This file is part of the Device Management System.
* It is responsible for displaying the list of devices and their details,
* and providing user-friendly actions for each device.
* use GET /api/v1/device/list to get the device list
* There are some fields that get from API in the device list:
* - clientId: The unique identifier for the device.
* - clientName: The name of the device.
* - status: The current status of the device (e.g., online, offline), perfer use icon to indicate.
* - updatedAt: The last time the device was updated.
* - createdAt: The time the device was created.
* I need the Action field to put some buttons in the table.
* - setting: The setting button to open the setting dialog.
* - dashboard: The dashboard button to open the dashboard dialog.
* - download: The download button to download the wav file.  
* The page should inlcude the following features:
* - pagination (25 rows per page)
* - sorting (default sort bye status, online first)
* - searching
* The status is a string, but I need to use icon to indicate the status.
* The status is real-time updated, so I need socket.io to update the status.
* the socketio event is "device_status_change" and the data is the device status.
*/

import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Container, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, TablePagination, TableSortLabel,
  TextField, InputAdornment, IconButton, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Snackbar, Alert
} from '@mui/material';
import { 
  Search as SearchIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Download as DownloadIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  Refresh as RefreshIcon,
  PowerSettingsNew as RebootIcon // Added RebootIcon
} from '@mui/icons-material';
import { useAuthContext } from '../hooks/useAuthContext';
import io from 'socket.io-client';
import DeviceSetting from './DeviceSetting';
import DashBoardContainer from '../components/DashBoardContainer';
import AudioManagementContainer from '../components/AudioManagementContainer';

const DeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { user } = useAuthContext();
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25); // Default 25 rows per page
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('status');
  const [order, setOrder] = useState('asc'); // 'asc' for online first
  
  // Dialog states
  const [settingDialogOpen, setSettingDialogOpen] = useState(false);
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(null);
  
  // Ref for DeviceSetting save function
  const deviceSettingSubmitRef = useRef(null);
  
  // Socket.io connection
  useEffect(() => {
    // Fetch devices initially
    fetchDevices();
    
    // Connect to socket.io server
    const socketUrtl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const socket = io(socketUrtl, { auth: { token: user?.token } });
    
    // Listen for real-time status updates
    socket.on('device_status_change', (data) => {
      // Update the device status in our state
      setDevices(prevDevices => 
        prevDevices.map(device => 
          device.clientId === data.clientId ? { ...device, clientName: data.clientName, status: data.status, updatedAt: data.timestamp, ...(data.version && { version: data.version }) } : device
        )
      );
    });
    
    // Clean up on unmount
    return () => {
      socket.disconnect();
    };
  }, [user]);
  
  // Update filtered devices when devices or search query changes
  useEffect(() => {
    filterAndSortDevices();
  }, [devices, searchQuery, orderBy, order]);
  
  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/device/list', {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setDevices(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to load devices: ${err.message}`);
      setLoading(false);
    }
  };
  
  const filterAndSortDevices = () => {
    // Filter devices based on search query
    const filtered = devices.filter(device => 
      device.clientId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      device.clientName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Sort devices
    const sorted = [...filtered].sort((a, b) => {
      const valueA = getComparableValue(a, orderBy);
      const valueB = getComparableValue(b, orderBy);
      
      // Special handling for status - online first
      if (orderBy === 'status') {
        const statusA = a.status?.toLowerCase() === 'online';
        const statusB = b.status?.toLowerCase() === 'online';
        return order === 'asc' 
          ? (statusB === statusA ? 0 : statusB ? 1 : -1)
          : (statusB === statusA ? 0 : statusB ? -1 : 1);
      }
      
      // General sorting
      if (valueA < valueB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredDevices(sorted);
  };
  
  const getComparableValue = (device, field) => {
    if (!device) return '';
    
    switch (field) {
      case 'updatedAt':
      case 'createdAt':
        return new Date(device[field] || 0);
      default:
        return (device[field] || '').toString().toLowerCase();
    }
  };
  
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSettingClick = (device) => {
    setCurrentDevice(device);
    setSettingDialogOpen(true);
  };
  
  const handleDashboardClick = (device) => {
    setCurrentDevice(device);
    setDashboardDialogOpen(true);
  };
  
  const handleDownloadClick = (device) => {
    setCurrentDevice(device);
    setDownloadDialogOpen(true);
  };

  const handleRebootClick = async (device) => {
    if (!device || !device.clientId) {
      setError("Device information is missing.");
      return;
    }

    // Add confirmation dialog before rebooting
    const confirmReboot = window.confirm(
      `Are you sure you want to reboot device "${device.clientName || device.clientId}"?`
    );

    if (!confirmReboot) {
      // User cancelled the reboot action
      setSuccess(null); // Clear any previous success messages
      setError("Reboot cancelled by user."); // Optional: inform user action was cancelled
      setTimeout(() => setError(null), 3000); // Clear message after a few seconds
      return;
    }

    setLoading(true); // Indicate an action is in progress
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`/api/v1/device/reboot/${device.clientId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user?.token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP error! Status: ${response.status}` }));
        throw new Error(errorData.message || `Failed to send reboot command. Status: ${response.status}`);
      }

      const result = await response.json();
      setSuccess(result.message || `Reboot command sent to ${device.clientName || device.clientId} successfully.`);
      // Optionally, you might want to refresh the device list or update status locally
      // fetchDevices(); 
    } catch (err) {
      console.error('Error sending reboot command:', err);
      setError(`Failed to reboot device: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDownload = () => {
    setSuccess(`Downloading WAV file for device ${currentDevice.clientId}`);
    setDownloadDialogOpen(false);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      // Add leading zero if needed
      const pad = (num) => num.toString().padStart(2, '0');
      
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1); // Months are 0-indexed
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (err) {
      console.error('Date formatting error:', err);
      return 'Error';
    }
  };
  
  const handleCloseSnackbar = () => {
    setSuccess(null);
    setError(null);
  };
  
  const handleSettingSave = async () => {
    console.log("Save button clicked");
    console.log("Ref:", deviceSettingSubmitRef.current); // 檢查 ref 是否存在
    
    if (deviceSettingSubmitRef.current) {
      try {
        // submit 是我們通過 useImperativeHandle 暴露的方法
        const success = await deviceSettingSubmitRef.current.submit();
        console.log("Submit result:", success);
        
        if (success) {
          setSuccess(`Settings for device ${currentDevice.clientId} saved successfully`);
          setSettingDialogOpen(false);
          fetchDevices();
        }
      } catch (error) {
        console.error("Error calling submit:", error);
      }
    } else {
      console.error("deviceSettingSubmitRef.current is null");
    }
  };
  
  return (
    <Container maxWidth="xl" sx={{ mb: 5 }}>
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Device List
        </Typography>
      </Box>
      
      {/* Success/Error Messages */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Devices</Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search devices..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDevices}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        
        {loading && devices.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'clientId'}
                        direction={orderBy === 'clientId' ? order : 'asc'}
                        onClick={() => handleRequestSort('clientId')}
                      >
                        Client ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'clientName'}
                        direction={orderBy === 'clientName' ? order : 'asc'}
                        onClick={() => handleRequestSort('clientName')}
                      >
                        Client Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={() => handleRequestSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'updatedAt'}
                        direction={orderBy === 'updatedAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('updatedAt')}
                      >
                        Updated At
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'createdAt'}
                        direction={orderBy === 'createdAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdAt')}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDevices.length > 0 ? (
                    filteredDevices
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((device) => (
                        <TableRow key={device.clientId} hover>
                          <TableCell>{device.clientId}</TableCell>
                          <TableCell>{device.clientName || 'N/A'}</TableCell>
                          <TableCell>
                            {device.status?.toLowerCase() === 'online' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: 'success.main',
                                    display: 'inline-block'
                                  }}
                                />
                                <Typography variant="body2">Online</Typography>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: 'error.main',
                                    display: 'inline-block'
                                  }}
                                />
                                <Typography variant="body2">Offline</Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}> {/* Ensure buttons don't wrap excessively */}
                              <Tooltip title="Settings">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSettingClick(device)}
                                >
                                  <SettingsIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Dashboard">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleDashboardClick(device)}
                                >
                                  <DashboardIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download WAV">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleDownloadClick(device)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reboot Device">
                                <IconButton
                                  size="small"
                                  color="warning" // Or 'error' depending on severity perception
                                  onClick={() => handleRebootClick(device)}
                                >
                                  <RebootIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>{formatDate(device.updatedAt)}</TableCell>
                          <TableCell>{formatDate(device.createdAt)}</TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {loading ? 'Loading...' : 'No devices found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredDevices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ width: '100%' }}
            />
          </>
        )}
      </Paper>
      
      {/* Settings Dialog */}
      <Dialog
        open={settingDialogOpen}
        onClose={() => setSettingDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Device Settings: {currentDevice?.clientName || currentDevice?.clientId}</DialogTitle>
        <DialogContent dividers>
          <DeviceSetting 
            device={currentDevice} 
            client_id={currentDevice?.clientId}
            onSaveSettings={(config) => {
              setSuccess(`Settings for device ${currentDevice.clientId} saved successfully`);
              setSettingDialogOpen(false);
              fetchDevices();
              return true;
            }} 
            ref={deviceSettingSubmitRef} // 設定 ref
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSettingSave}
          >
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dashboard Dialog */}
      <Dialog
        open={dashboardDialogOpen}
        onClose={() => setDashboardDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Dashboard: {currentDevice?.clientName || currentDevice?.clientId}</DialogTitle>
        <DialogContent dividers>
          {/* Dashboard content goes here */}
          {/* <Typography variant="body1">
            Dashboard for device {currentDevice?.clientId}
          </Typography> */}
          <DashBoardContainer clientId={currentDevice?.clientId} title="" />
          {/* Add dashboard visualizations here */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashboardDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Download Dialog */}
      <Dialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '90vw',
            height: '90vh',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>Download WAV File: {currentDevice?.clientName || currentDevice?.clientId}</DialogTitle>
        <DialogContent dividers>
          <AudioManagementContainer clientId={currentDevice?.clientId} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeviceList;
